import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop - Automatically scrolls to the top of the page when navigating between routes
 * This helps improve user experience by ensuring new pages start at the top
 * Also handles hash navigation for smooth scrolling to sections (for same-page navigation)
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const previousPathname = useRef<string | null>(null);
  const previousHash = useRef<string | null>(null);

  useEffect(() => {
    const pathnameChanged = previousPathname.current !== pathname;
    const hashChanged = previousHash.current !== hash;
    const isSamePage = !pathnameChanged && hashChanged && hash;

    // Update refs
    previousPathname.current = pathname;
    previousHash.current = hash;

    // If there's a hash and we're on the same page (only hash changed), scroll immediately
    if (isSamePage && hash) {
      const scrollToHash = () => {
        const element = document.querySelector(hash);
        if (element) {
          requestAnimationFrame(() => {
            // Get header height
            const header = document.querySelector('header');
            const headerHeight = header ? header.offsetHeight : 120;
            
            // Get element position
            const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementTop - headerHeight - 20;
            
            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'smooth'
            });
          });
          return true;
        }
        return false;
      };

      // Try immediately, then with a short delay
      if (!scrollToHash()) {
        setTimeout(scrollToHash, 100);
      }
    } else if (!hash && pathnameChanged) {
      // No hash, scroll to top on route change
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    }
    // For cross-page navigation with hash, MainLayout handles it after loading
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop; 